import axios from 'axios';

let _isLoggingOut = false;
const navigateToLogin = () => {
  if (_isLoggingOut) return;       // prevent alert storm from multiple 401s
  _isLoggingOut = true;
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('lu');
  sessionStorage.removeItem('auth');
  sessionStorage.removeItem('saadhakProfile');
  localStorage.removeItem('auth');  // clear stale auth so PrivateRoute redirects cleanly
  localStorage.removeItem('myc_pwa_dismissed_at');
  window.location.href = '/login';
};

/* ── GPS position cache (in-memory only) ─────────────────────────────────────
 * Cached position is attached to every API request as x-userlat / x-userlon
 * so the backend geo-fence can validate location on every call (not just login).
 * Starts null — set by Login.js via updateGpsCache() after getGeoCoords().
 * Refreshes every 3 minutes in the background once permission is granted.
 * No sessionStorage — each tab/login starts fresh (avoids cross-user stale coords).
 */
// Seed from sessionStorage so GPS is available immediately on page refresh
let _gps = (() => {
  try {
    var s = sessionStorage.getItem('lastGps');
    return s ? JSON.parse(s) : null;
  } catch (e) { return null; }
})();

// Max age (ms) beyond which cached GPS is considered stale and NOT sent in headers
var GPS_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
// Max accuracy (metres) beyond which GPS is considered unreliable
var GPS_MAX_ACCURACY_M = 150;

async function _refreshGps() {
  if (!navigator.geolocation) return;
  try {
    if (navigator.permissions) {
      var perm = await navigator.permissions.query({ name: 'geolocation' });
      if (perm.state === 'denied') return;
      if (perm.state === 'prompt') return;   // don't trigger browser prompt — login handles that
    }
  } catch (e) { /* permissions API not supported — fall through */ }
  navigator.geolocation.getCurrentPosition(
    function(pos) {
      _gps = {
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        accuracy: pos.coords.accuracy || 9999,
        ts: Date.now(),
      };
      try { sessionStorage.setItem('lastGps', JSON.stringify(_gps)); } catch (e) {}
    },
    function() {},
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
}

/** Force-refresh GPS and return a promise that resolves with coords (or null) */
function _forceRefreshGps() {
  if (!navigator.geolocation) return Promise.resolve(null);
  return new Promise(function(resolve) {
    navigator.geolocation.getCurrentPosition(
      function(pos) {
        _gps = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy || 9999,
          ts: Date.now(),
        };
        try { sessionStorage.setItem('lastGps', JSON.stringify(_gps)); } catch (e) {}
        resolve(_gps);
      },
      function() { resolve(null); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
}

_refreshGps();
setInterval(_refreshGps, 2 * 60 * 1000); // refresh every 2 min (was 3)

/** Reset the logout guard — call from Login.js on mount so fresh login works. */
export function resetLogoutGuard() { _isLoggingOut = false; }

/**
 * Immediately set the in-memory GPS cache.
 * Called from Login.js after getGeoCoords() so post-login API calls
 * use the same accurate coords that passed the login geo-fence.
 */
export function updateGpsCache(lat, lon, accuracy) {
  _gps = { lat: lat, lon: lon, accuracy: accuracy || 0, ts: Date.now() };
  try { sessionStorage.setItem('lastGps', JSON.stringify(_gps)); } catch (e) {}
}

/** Return current cached GPS coords (or null if not yet obtained). */
export function getGpsCache() {
  return _gps;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_URL,
  withCredentials: true, // Send httpOnly cookie on every request
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Sends combined consultation data to the backend
 *
 * @param {Object} params
 * @param {Event} params.event - DOM event
 * @param {string} params.stype - Section type (e.g., 'Knee', 'Neck')
 * @param {string} params.patientCard - Patient ID or card reference
 * @param {Array} params.sequences - Sequence data
 * @param {Array} params.recommendations - Recommendation data
 * @param {Array} params.services - Services data
 * @returns {Promise<boolean>} true on success, false on failure
 */
export const sendCombinedChangeCommon = async (  event,  stype,  patientCard,  payload) => {
  if (event?.preventDefault) event.preventDefault();
  console.log("combinedChange =", patientCard, stype, payload);

  try {
    const response = await api.post(
      `${process.env.REACT_APP_URL}/patients/consultation/combined/${patientCard}`,
      payload
    );

    console.log("✅ Data sent successfully:", response.data);
    alert("Updated successfully!");
    return true;
  } catch (error) {
    console.error("❌ Error sending data:", error);
    alert("Error updating data. Please try again!");
    return false;
  }
};

// Request Interceptor — attaches username and cached GPS coords
// Token is now in httpOnly cookie (sent automatically by browser).
// Authorization header is TRANSITION FALLBACK for old logins that still have token in localStorage.
api.interceptors.request.use(
  (config) => {
    // Read from localStorage (works for both old format with token and new UI-hints-only format)
    var authHints = (function() {
      try { return JSON.parse(localStorage.getItem('auth') || '{}'); } catch(e) { return {}; }
    })();
    // x-username still needed (backend validates it against JWT)
    if (authHints.username) config.headers['x-username'] = authHints.username;
    // Also check sessionStorage for backward compat (old sessions before this update)
    if (!authHints.username) {
      var lu = sessionStorage.getItem('lu');
      if (lu) config.headers['x-username'] = lu;
    }

    // TRANSITION FALLBACK: if localStorage still has a token (old login before cookie update),
    // send it as Authorization header so backend header-fallback accepts it.
    // New logins set httpOnly cookie instead — no token in localStorage.
    // After 120 days all old tokens expire → this fallback becomes a no-op.
    if (authHints.token) {
      config.headers['Authorization'] = 'Bearer ' + authHints.token;
    } else {
      // Also check sessionStorage for old sessions
      var sessToken = sessionStorage.getItem('token');
      if (sessToken) config.headers['Authorization'] = 'Bearer ' + sessToken;
    }

    // Attach last-known GPS so backend can geo-fence every request
    // Skip if GPS is too old (>5 min) or too inaccurate (>150m) — fail-open is safer
    // Also skip if ts is missing (old-format cache before this update) — treat as stale
    if (_gps && _gps.lat && _gps.lon && _gps.ts) {
      var gpsAge = Date.now() - _gps.ts;
      var gpsAccuracy = typeof _gps.accuracy === 'number' ? _gps.accuracy : 9999;
      if (gpsAge < GPS_MAX_AGE_MS && gpsAccuracy < GPS_MAX_ACCURACY_M) {
        config.headers['x-userlat'] = String(_gps.lat);
        config.headers['x-userlon'] = String(_gps.lon);
      }
      // else: don't send coords → backend will fail-open (no coords = allow)
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Auto-recover from IP mismatch (mobile networks rotate IPs) ────────────
// When backend returns 401 + ipBlocked, call session-restore to re-register
// the new IP, then transparently retry the original request. Only logout if
// the token itself is expired/invalid.
let _isRestoringSession = false;
let _restorePromise = null;

function _doSessionRestore() {
  if (_restorePromise) return _restorePromise;
  _isRestoringSession = true;
  var authHints = (function() {
    try { return JSON.parse(localStorage.getItem('auth') || '{}'); } catch(e) { return {}; }
  })();
  var lu = authHints.username || sessionStorage.getItem('lu') || '';
  var token = authHints.token || sessionStorage.getItem('token') || '';
  var base = process.env.REACT_APP_URL || '';
  var headers = { 'Content-Type': 'application/json', 'x-username': lu };
  // Send Authorization header as fallback for old sessions; cookie sent automatically via credentials
  if (token) headers['Authorization'] = 'Bearer ' + token;
  _restorePromise = fetch(base + '/users/session-restore', {
    method: 'POST',
    credentials: 'include', // send httpOnly cookie
    headers: headers,
  })
    .then(function (r) { return r.ok; })
    .catch(function () { return false; })
    .then(function (ok) {
      _isRestoringSession = false;
      _restorePromise = null;
      return ok;
    });
  return _restorePromise;
}

// Response Interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status, data, config } = error.response;
      const url = config?.url || '';

      if (status === 401) {
        // Skip interceptor for login/auth endpoints — let the Login component
        // handle these errors inline (wrong password, wrong OTP, expired OTP, etc.)
        // Also skip session-check — Login.js handles its own 401 via .catch()
        const isAuthEndpoint = url.includes('/login') || url.includes('/verify-login-otp')
          || url.includes('/saadhak-otp/') || url.includes('/saadhaklogin')
          || url.includes('/kiosk-otp/') || url.includes('/session-check');
        if (!isAuthEndpoint && !url.includes('/gdrive/') && !_isLoggingOut) {
          // IP changed (mobile network rotation) — try session-restore + retry once
          if (data?.ipBlocked && !config._ipRetried) {
            const restored = await _doSessionRestore();
            if (restored) {
              config._ipRetried = true;
              return api(config); // retry original request with same config
            }
          }
          // Token genuinely expired OR session-restore failed — log out
          _isLoggingOut = true;
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('lu');
          sessionStorage.removeItem('auth');
          sessionStorage.removeItem('saadhakProfile');
          localStorage.removeItem('auth');
          const msg = data?.ipBlocked
            ? (data.message || 'Session invalid: network changed. Please log in again.')
            : 'Session Expired / Unauthorized access';
          alert(msg);
          window.location.href = '/login';
        }
      }

      // GDrive disconnected — broadcast global event so any page can show banner
      if (status === 503 && data?.error === 'GDRIVE_REAUTH') {
        window.dispatchEvent(new CustomEvent('gdrive-disconnected'));
      }

      if (status === 403 && data && data.geoBlocked) {
        // Skip alert for background geo-ping requests
        var reqUrl = config && config.url ? config.url : '';
        if (reqUrl.includes('/geo-ping')) return Promise.reject(error);

        // Auto-retry ONCE: refresh GPS and retry the request with fresh coords
        if (!config._geoRetried) {
          return _forceRefreshGps().then(function(freshGps) {
            if (freshGps) {
              config._geoRetried = true;
              // Remove old GPS headers — let interceptor re-attach from fresh _gps
              // (interceptor applies accuracy + age checks so bad GPS won't be sent)
              delete config.headers['x-userlat'];
              delete config.headers['x-userlon'];
              return api(config);
            }
            // GPS refresh failed — show alert
            var now2 = Date.now();
            if (!api._lastGeoAlert || now2 - api._lastGeoAlert > 10000) {
              api._lastGeoAlert = now2;
              alert(data.message || 'Access denied: you appear to be outside the authorised area. Please ensure you are at the centre and try again.');
            }
            return Promise.reject(error);
          });
        }

        // Already retried — show alert
        var now = Date.now();
        if (!api._lastGeoAlert || now - api._lastGeoAlert > 10000) {
          api._lastGeoAlert = now;
          alert(data.message || 'Access denied: you appear to be outside the authorised area. Please ensure you are at the centre and try again.');
        }
        // Do NOT navigate to login — just block the action
      }

      if (status === 400 && data?.message) {
        // Skip alert for login/auth endpoints — let their own catch blocks handle it
        const isAuthEndpoint = url.includes('/login') || url.includes('/verify-login-otp')
          || url.includes('/saadhak-otp/') || url.includes('/saadhaklogin')
          || url.includes('/kiosk-otp/');
        if (!isAuthEndpoint) alert(data.message);
      }
    }

    return Promise.reject(error);
  }
);

/* ── Geo-ping: 20-min interval while attendance is IN_PROGRESS ──────────────
 * Starts on login/check-in. Sends GPS to backend for geo-presence tracking.
 * Backend alerts owner/solution if user is outside fence for 4+ consecutive pings.
 */
let _geoPingInterval = null;

export function startGeoPingIfCheckedIn() {
  if (_geoPingInterval) { clearInterval(_geoPingInterval); _geoPingInterval = null; }

  // Read username from localStorage (new) or sessionStorage (old transition)
  var authHints = (function() {
    try { return JSON.parse(localStorage.getItem('auth') || '{}'); } catch(e) { return {}; }
  })();
  const username = authHints.username || sessionStorage.getItem('lu');
  if (!username) return;

  api.get(`${process.env.REACT_APP_URL}/myattendance/today/${username}`)
    .then(({ data }) => {
      if (data?.status !== 'IN_PROGRESS') return;

      const sendPing = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            api.post(`${process.env.REACT_APP_URL}/myattendance/geo-ping`, {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            }).catch(() => {});
          },
          () => {},
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      };

      sendPing();
      _geoPingInterval = setInterval(sendPing, 20 * 60 * 1000);
    })
    .catch(() => {});
}

export function stopGeoPing() {
  if (_geoPingInterval) { clearInterval(_geoPingInterval); _geoPingInterval = null; }
}

export default api;
